export type ProblemTimingSnapshotV2 = {
  startedAt: string;
  completedAt: string;
  elapsedDurationMs: number;
  activeDurationMs: number;
};

type ProblemTimingRecord = {
  firstAtMs: number;
  activeAccumulatedMs: number;
  activeStartedAtMs: number | null;
  lastAtMs: number;
};

type Clock = () => number;

export type ProblemActivityPersistedStateV2 = {
  records: Record<string, {
    firstAtMs: number;
    activeAccumulatedMs: number;
    lastAtMs: number;
  }>;
};

/**
 * Low-volume problem timing. One problem is considered active at a time.
 * Pointer movement is never recorded; switching/focusing a problem is enough
 * to move the active timing segment.
 */
export class ProblemActivityTrackerV2 {
  private readonly records = new Map<string, ProblemTimingRecord>();
  private activeProblemId: string | null = null;

  constructor(private readonly now: Clock = () => Date.now(), initialState?: ProblemActivityPersistedStateV2) {
    if (initialState) {
      for (const [problemId, record] of Object.entries(initialState.records)) {
        this.records.set(problemId, {
          firstAtMs: record.firstAtMs,
          activeAccumulatedMs: record.activeAccumulatedMs,
          activeStartedAtMs: null,
          lastAtMs: record.lastAtMs,
        });
      }
    }
  }

  exportState(atMs = this.now()): ProblemActivityPersistedStateV2 {
    const records: ProblemActivityPersistedStateV2['records'] = {};
    for (const [problemId, record] of this.records.entries()) {
      const activeTail = this.activeProblemId === problemId && record.activeStartedAtMs !== null
        ? Math.max(0, atMs - record.activeStartedAtMs)
        : 0;
      records[problemId] = {
        firstAtMs: record.firstAtMs,
        activeAccumulatedMs: record.activeAccumulatedMs + activeTail,
        lastAtMs: Math.max(record.lastAtMs, atMs),
      };
    }
    return { records };
  }

  activate(problemId: string) {
    const current = this.now();
    if (this.activeProblemId === problemId) {
      const record = this.records.get(problemId);
      if (record) record.lastAtMs = current;
      return;
    }
    this.stopActive(current);
    const existing = this.records.get(problemId);
    if (existing) {
      existing.activeStartedAtMs = current;
      existing.lastAtMs = current;
    } else {
      this.records.set(problemId, {
        firstAtMs: current,
        activeAccumulatedMs: 0,
        activeStartedAtMs: current,
        lastAtMs: current,
      });
    }
    this.activeProblemId = problemId;
  }

  pause() {
    this.stopActive(this.now());
  }

  snapshot(problemId: string, completedAtMs = this.now()): ProblemTimingSnapshotV2 | null {
    const record = this.records.get(problemId);
    if (!record) return null;
    const activeTail = this.activeProblemId === problemId && record.activeStartedAtMs !== null
      ? Math.max(0, completedAtMs - record.activeStartedAtMs)
      : 0;
    const completed = Math.max(record.firstAtMs, completedAtMs);
    return {
      startedAt: new Date(record.firstAtMs).toISOString(),
      completedAt: new Date(completed).toISOString(),
      elapsedDurationMs: Math.max(0, completed - record.firstAtMs),
      activeDurationMs: record.activeAccumulatedMs + activeTail,
    };
  }

  private stopActive(current: number) {
    if (!this.activeProblemId) return;
    const record = this.records.get(this.activeProblemId);
    if (record?.activeStartedAtMs !== null && record?.activeStartedAtMs !== undefined) {
      record.activeAccumulatedMs += Math.max(0, current - record.activeStartedAtMs);
      record.activeStartedAtMs = null;
      record.lastAtMs = current;
    }
    this.activeProblemId = null;
  }
}
